import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { API } from '../App';
import AiLocked from '../components/AiLocked';
import { Loader2, Sparkles, Send } from 'lucide-react';

export default function CareerCoachPage() {
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [messages, setMessages] = useState([]);
  const [sessionId, setSessionId] = useState(null);
  const [input, setInput] = useState('');
  const [targetRole, setTargetRole] = useState('');
  const [sending, setSending] = useState(false);
  const endRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/ai/usage`).then(r => setUsage(r.data.ai_coach)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const send = async () => {
    const msg = input.trim();
    if (!msg) return;
    setInput('');
    setMessages(m => [...m, { role: 'user', content: msg }]);
    setSending(true);
    try {
      const r = await axios.post(`${API}/ai/coach/chat`, { message: msg, session_id: sessionId, target_role: targetRole || undefined });
      setSessionId(r.data.session_id);
      setMessages(m => [...m, { role: 'assistant', content: r.data.reply }]);
      setUsage(u => ({ ...u, remaining: r.data.remaining }));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Coach unavailable');
      setMessages(m => m.slice(0, -1));
      setInput(msg);
    } finally { setSending(false); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-[#CE1126]" /></div>;

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Header />
      <main className="flex-1 container mx-auto px-4 py-8 max-w-3xl w-full">
        <div className="flex items-center gap-2 mb-6">
          <Sparkles className="w-6 h-6 text-[#CE1126]" />
          <h1 className="text-2xl font-bold">Advanced AI Career Coach</h1>
        </div>

        {!usage?.has_access ? (
          <AiLocked title="Advanced AI Career Coach" />
        ) : (
          <Card className="flex flex-col h-[70vh]" data-testid="coach-panel">
            <div className="p-3 border-b flex flex-wrap items-center gap-2 justify-between">
              <input value={targetRole} onChange={e => setTargetRole(e.target.value)} placeholder="Target role (optional)"
                className="h-9 rounded-md border border-gray-300 px-3 text-sm flex-1 min-w-[150px]" data-testid="coach-role-input" />
              <span className="text-xs text-gray-500" data-testid="coach-remaining">{usage.remaining} of {usage.limit} interactions left</span>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="coach-messages">
              {messages.length === 0 && (
                <p className="text-sm text-gray-400 text-center mt-8">Ask anything about your UAE job search — CV, LinkedIn, interviews, skills, or follow-ups.</p>
              )}
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[85%] rounded-2xl px-4 py-2 text-sm whitespace-pre-wrap ${m.role === 'user' ? 'bg-[#CE1126] text-white' : 'bg-gray-100 text-gray-900'}`}>
                    {m.content}
                  </div>
                </div>
              ))}
              {sending && <div className="flex justify-start"><div className="bg-gray-100 rounded-2xl px-4 py-2"><Loader2 className="w-4 h-4 animate-spin text-gray-500" /></div></div>}
              <div ref={endRef} />
            </div>
            <div className="p-3 border-t flex gap-2">
              <input value={input} onChange={e => setInput(e.target.value)} onKeyDown={e => { if (e.key === 'Enter' && !sending) send(); }}
                placeholder="Type your question…" disabled={usage.remaining <= 0}
                className="flex-1 h-10 rounded-md border border-gray-300 px-3 text-sm" data-testid="coach-input" />
              <Button className="btn-uae-red" onClick={send} disabled={sending || usage.remaining <= 0} data-testid="coach-send-btn"><Send className="w-4 h-4" /></Button>
            </div>
            {usage.remaining <= 0 && <p className="text-xs text-red-600 px-3 pb-2">You've used all coach interactions for this package.</p>}
          </Card>
        )}
      </main>
      <Footer />
    </div>
  );
}
